import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

function DevDashboard() {
  const token = localStorage.getItem("token") || "";
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [whitelist, setWhitelist] = useState([]);
  const [groups, setGroups] = useState(null);
  const [chatLogs, setChatLogs] = useState([]);
  const [newPhone, setNewPhone] = useState("");
  const [newName, setNewName] = useState("");
  const [logFilter, setLogFilter] = useState("all");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }), [token]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [statsRes, settingsRes, whitelistRes, groupsRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/dev/stats`, { headers }),
        fetch(`${API_URL}/dev/settings`, { headers }),
        fetch(`${API_URL}/dev/whitelist`, { headers }),
        fetch(`${API_URL}/dev/groups`, { headers }),
        fetch(`${API_URL}/dev/chat-logs?limit=100${logFilter !== "all" ? `&agent_type=${logFilter}` : ""}`, { headers }),
      ]);

      if (statsRes.status === 401 || statsRes.status === 403) {
        toast.error("Akses dev hanya untuk admin/developer");
        return;
      }

      setStats(await statsRes.json());
      setSettings(await settingsRes.json());
      const wlData = await whitelistRes.json();
      setWhitelist(wlData.whitelist || []);
      setGroups(await groupsRes.json());
      const logData = await logsRes.json();
      setChatLogs(logData.logs || []);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  }, [token, logFilter, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (token) { const iv = setInterval(fetchData, 15000); return () => clearInterval(iv); } }, [token, fetchData]);

  const addWhitelist = async () => {
    if (!newPhone.trim()) return toast.error("Masukkan nomor telepon");
    try {
      const res = await fetch(`${API_URL}/dev/whitelist`, {
        method: "POST", headers,
        body: JSON.stringify({ phone: newPhone.trim(), name: newName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(`Nomor ${newPhone} ditambahkan`);
      setNewPhone(""); setNewName("");
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const removeWhitelist = async (phone) => {
    if (!window.confirm(`Hapus ${phone} dari whitelist?`)) return;
    try {
      await fetch(`${API_URL}/dev/whitelist/${phone}`, { method: "DELETE", headers });
      toast.success("Nomor dihapus");
      fetchData();
    } catch (err) { toast.error("Gagal menghapus"); }
  };

  const setGrupId = async (type, id) => {
    try {
      await fetch(`${API_URL}/dev/settings`, {
        method: "PUT", headers,
        body: JSON.stringify({ [`grup_${type}_id`]: id }),
      });
      toast.success(`Grup ${type} di-set!`);
      fetchData();
    } catch (err) { toast.error("Gagal mengatur grup"); }
  };

  const toggleWhitelist = async () => {
    try {
      await fetch(`${API_URL}/dev/settings`, {
        method: "PUT", headers,
        body: JSON.stringify({ whitelist_enabled: !settings?.whitelist_enabled }),
      });
      toast.success(settings?.whitelist_enabled ? "Whitelist dinonaktifkan" : "Whitelist diaktifkan");
      fetchData();
    } catch (err) { toast.error("Gagal"); }
  };

  if (!token) return null;

  const tabs = [
    { id: "overview", label: "📊 Overview", },
    { id: "whitelist", label: "📱 Whitelist" },
    { id: "groups", label: "🏷️ Grup" },
    { id: "logs", label: "💬 Chat Logs" },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>⚙️ Developer Dashboard</h1>
          <p style={styles.headerSub}>Kantor Notaris Erwin Suhardiman</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ ...styles.tab, ...(activeTab === t.id ? styles.tabActive : {}) }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={styles.content}>

        {/* ===== OVERVIEW TAB ===== */}
        {activeTab === "overview" && stats && (
          <div>
            <div style={styles.statsGrid}>
              <div style={{ ...styles.statCard, borderLeft: "4px solid #10b981" }}>
                <div style={styles.statValue}>{stats.bot_online ? "🟢 Online" : "🔴 Offline"}</div>
                <div style={styles.statLabel}>Status Bot</div>
              </div>
              <div style={{ ...styles.statCard, borderLeft: "4px solid #3b82f6" }}>
                <div style={styles.statValue}>{stats.total_chats}</div>
                <div style={styles.statLabel}>Total Chat</div>
              </div>
              <div style={{ ...styles.statCard, borderLeft: "4px solid #f59e0b" }}>
                <div style={styles.statValue}>{stats.today_chats}</div>
                <div style={styles.statLabel}>Chat Hari Ini</div>
              </div>
              <div style={{ ...styles.statCard, borderLeft: "4px solid #8b5cf6" }}>
                <div style={styles.statValue}>{stats.whitelist_count}</div>
                <div style={styles.statLabel}>Whitelist</div>
              </div>
            </div>

            <div style={styles.statsGrid}>
              <div style={{ ...styles.statCard, borderLeft: "4px solid #06b6d4" }}>
                <div style={styles.statValue}>🗂️ {stats.arsip_chats}</div>
                <div style={styles.statLabel}>Chat Arsip (Gemini)</div>
              </div>
              <div style={{ ...styles.statCard, borderLeft: "4px solid #ec4899" }}>
                <div style={styles.statValue}>🧮 {stats.pajak_chats}</div>
                <div style={styles.statLabel}>Chat Pajak (OpenRouter)</div>
              </div>
            </div>

            <div style={styles.infoBox}>
              <h3 style={styles.infoTitle}>📋 Arsitektur Multi-Agent</h3>
              <div style={styles.agentGrid}>
                <div style={styles.agentCard}>
                  <div style={styles.agentIcon}>🗂️</div>
                  <h4>Agent Arsip</h4>
                  <p style={styles.agentDesc}>Engine: <b>Google Gemini</b></p>
                  <p style={styles.agentDesc}>Skill: Cari dokumen, statistik, ambil file</p>
                  <p style={styles.agentDesc}>Grup: {settings?.grup_arsip_id ? "✅ Dikonfigurasi" : "⚠️ Belum diset"}</p>
                </div>
                <div style={styles.agentCard}>
                  <div style={styles.agentIcon}>🧮</div>
                  <h4>Agent Pajak</h4>
                  <p style={styles.agentDesc}>Engine: <b>OpenRouter</b></p>
                  <p style={styles.agentDesc}>Skill: BPHTB, PPh, PPN, biaya notaris</p>
                  <p style={styles.agentDesc}>Grup: {settings?.grup_pajak_id ? "✅ Dikonfigurasi" : "⚠️ Belum diset"}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== WHITELIST TAB ===== */}
        {activeTab === "whitelist" && (
          <div>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>📱 Whitelist Nomor Telepon</h2>
              <button onClick={toggleWhitelist}
                style={{ ...styles.btnToggle, background: settings?.whitelist_enabled ? "#10b981" : "#6b7280" }}>
                {settings?.whitelist_enabled ? "✅ Aktif" : "⛔ Nonaktif"}
              </button>
            </div>

            {!settings?.whitelist_enabled && (
              <div style={styles.warningBox}>
                ⚠️ Whitelist dinonaktifkan — semua nomor bisa chat ke bot
              </div>
            )}

            <div style={styles.addForm}>
              <input type="text" placeholder="Nomor (0812xxx atau 62812xxx)" value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)} style={styles.inputSmall} />
              <input type="text" placeholder="Nama (opsional)" value={newName}
                onChange={(e) => setNewName(e.target.value)} style={styles.inputSmall} />
              <button onClick={addWhitelist} style={styles.btnAdd}>+ Tambah</button>
            </div>

            <div style={styles.list}>
              {whitelist.length === 0 ? (
                <div style={styles.empty}>Belum ada nomor di whitelist. Semua nomor bisa chat.</div>
              ) : whitelist.map((w, i) => (
                <div key={i} style={styles.listItem}>
                  <div>
                    <span style={styles.phone}>{w.phone}</span>
                    {w.name && <span style={styles.name}> — {w.name}</span>}
                    <div style={styles.date}>Ditambahkan: {new Date(w.added_at).toLocaleDateString("id")}</div>
                  </div>
                  <button onClick={() => removeWhitelist(w.phone)} style={styles.btnDelete}>🗑️</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== GROUPS TAB ===== */}
        {activeTab === "groups" && groups && (
          <div>
            <h2 style={styles.sectionTitle}>🏷️ Konfigurasi Grup WhatsApp</h2>

            <div style={styles.groupConfig}>
              <div style={styles.groupCard}>
                <h3>🗂️ Grup Arsip</h3>
                <p style={styles.groupId}>{settings?.grup_arsip_id || "Belum diset"}</p>
                {settings?.grup_arsip_id && (
                  <button onClick={() => setGrupId("arsip", "")} style={styles.btnSmallDanger}>Reset</button>
                )}
              </div>
              <div style={styles.groupCard}>
                <h3>🧮 Grup Pajak</h3>
                <p style={styles.groupId}>{settings?.grup_pajak_id || "Belum diset"}</p>
                {settings?.grup_pajak_id && (
                  <button onClick={() => setGrupId("pajak", "")} style={styles.btnSmallDanger}>Reset</button>
                )}
              </div>
            </div>

            <h3 style={{ ...styles.sectionTitle, marginTop: 24 }}>📡 Grup Terdeteksi Otomatis</h3>
            <p style={styles.helpText}>
              Bot akan otomatis mendeteksi semua grup WA yang dia masuki.
              Klik tombol untuk assign grup ke agent.
            </p>

            <div style={styles.list}>
              {(!groups.detected_groups || groups.detected_groups.length === 0) ? (
                <div style={styles.empty}>
                  Belum ada grup terdeteksi. Kirim pesan di grup WA yang sudah ada bot-nya.
                </div>
              ) : groups.detected_groups.map((g, i) => (
                <div key={i} style={styles.listItem}>
                  <div>
                    <span style={styles.phone}>{g.name}</span>
                    <div style={styles.date}>ID: {g.id}</div>
                    <div style={styles.date}>
                      Terdeteksi: {new Date(g.detected_at).toLocaleDateString("id")}
                      {g.id === settings?.grup_arsip_id && <span style={styles.badge}>🗂️ ARSIP</span>}
                      {g.id === settings?.grup_pajak_id && <span style={styles.badgePajak}>🧮 PAJAK</span>}
                    </div>
                  </div>
                  <div style={styles.groupActions}>
                    <button onClick={() => setGrupId("arsip", g.id)}
                      style={{ ...styles.btnSmall, background: g.id === settings?.grup_arsip_id ? "#10b981" : "#3b82f6" }}>
                      🗂️ Arsip
                    </button>
                    <button onClick={() => setGrupId("pajak", g.id)}
                      style={{ ...styles.btnSmall, background: g.id === settings?.grup_pajak_id ? "#10b981" : "#8b5cf6" }}>
                      🧮 Pajak
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== CHAT LOGS TAB ===== */}
        {activeTab === "logs" && (
          <div>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>💬 Chat Logs</h2>
              <div style={styles.filterGroup}>
                {["all", "arsip", "pajak"].map((f) => (
                  <button key={f} onClick={() => setLogFilter(f)}
                    style={{ ...styles.filterBtn, ...(logFilter === f ? styles.filterBtnActive : {}) }}>
                    {f === "all" ? "Semua" : f === "arsip" ? "🗂️ Arsip" : "🧮 Pajak"}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.logList}>
              {chatLogs.length === 0 ? (
                <div style={styles.empty}>Belum ada chat log.</div>
              ) : chatLogs.map((log, i) => (
                <div key={i} style={styles.logItem}>
                  <div style={styles.logHeader}>
                    <span style={log.agent_type === "pajak" ? styles.badgePajak : styles.badge}>
                      {log.agent_type === "pajak" ? "🧮 Pajak" : "🗂️ Arsip"}
                    </span>
                    <span style={styles.logPhone}>{log.phone?.split("@")[0]}</span>
                    <span style={styles.logTime}>{new Date(log.timestamp).toLocaleString("id")}</span>
                  </div>
                  <div style={styles.logMsg}>
                    <strong>User:</strong> {log.message}
                  </div>
                  <div style={styles.logReply}>
                    <strong>Bot:</strong> {log.reply?.substring(0, 200)}{log.reply?.length > 200 ? "..." : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Styles
// ============================================================
const styles = {
  // Login
  loginContainer: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", padding: 20 },
  loginCard: { background: "#1e293b", borderRadius: 16, padding: 40, width: "100%", maxWidth: 400, textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", border: "1px solid #334155" },
  loginIcon: { fontSize: 48, marginBottom: 16 },
  loginTitle: { color: "#f1f5f9", fontSize: 24, margin: "0 0 8px", fontWeight: 700 },
  loginSubtitle: { color: "#94a3b8", fontSize: 14, margin: "0 0 24px" },
  loginForm: { display: "flex", flexDirection: "column", gap: 12 },
  input: { padding: "12px 16px", borderRadius: 8, border: "1px solid #334155", background: "#0f172a", color: "#f1f5f9", fontSize: 14, outline: "none" },

  // Layout
  container: { minHeight: "100vh", background: "#0f172a", color: "#f1f5f9", fontFamily: "'Inter', -apple-system, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid #1e293b", background: "#0f172a" },
  headerTitle: { margin: 0, fontSize: 22, fontWeight: 700, color: "#f1f5f9" },
  headerSub: { margin: "4px 0 0", fontSize: 13, color: "#94a3b8" },

  // Tabs
  tabs: { display: "flex", gap: 4, padding: "12px 24px", borderBottom: "1px solid #1e293b", overflowX: "auto" },
  tab: { padding: "10px 20px", borderRadius: 8, border: "none", background: "transparent", color: "#94a3b8", cursor: "pointer", fontSize: 14, fontWeight: 500, whiteSpace: "nowrap", transition: "all 0.2s" },
  tabActive: { background: "#1e293b", color: "#f1f5f9", boxShadow: "0 0 0 1px #334155" },

  content: { padding: 24 },

  // Stats
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 16 },
  statCard: { background: "#1e293b", borderRadius: 12, padding: 20, border: "1px solid #334155" },
  statValue: { fontSize: 28, fontWeight: 700, color: "#f1f5f9", marginBottom: 4 },
  statLabel: { fontSize: 13, color: "#94a3b8" },

  // Info box
  infoBox: { background: "#1e293b", borderRadius: 12, padding: 24, border: "1px solid #334155", marginTop: 8 },
  infoTitle: { margin: "0 0 16px", fontSize: 16, fontWeight: 600 },
  agentGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  agentCard: { background: "#0f172a", borderRadius: 10, padding: 20, border: "1px solid #334155" },
  agentIcon: { fontSize: 32, marginBottom: 8 },
  agentDesc: { margin: "4px 0", fontSize: 13, color: "#94a3b8" },

  // Section
  sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 },
  sectionTitle: { margin: 0, fontSize: 18, fontWeight: 600 },

  // Buttons
  btnPrimary: { padding: "12px", borderRadius: 8, border: "none", background: "#3b82f6", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  btnLogout: { padding: "8px 16px", borderRadius: 8, border: "1px solid #475569", background: "transparent", color: "#94a3b8", fontSize: 13, cursor: "pointer" },
  btnToggle: { padding: "8px 16px", borderRadius: 8, border: "none", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnAdd: { padding: "10px 20px", borderRadius: 8, border: "none", background: "#10b981", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" },
  btnDelete: { padding: "8px 12px", borderRadius: 8, border: "1px solid #475569", background: "transparent", cursor: "pointer", fontSize: 16 },
  btnSmall: { padding: "6px 12px", borderRadius: 6, border: "none", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnSmallDanger: { padding: "6px 12px", borderRadius: 6, border: "none", background: "#ef4444", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", marginTop: 8 },

  // Forms
  addForm: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" },
  inputSmall: { padding: "10px 14px", borderRadius: 8, border: "1px solid #334155", background: "#0f172a", color: "#f1f5f9", fontSize: 13, flex: 1, minWidth: 140, outline: "none" },

  // Lists
  list: { display: "flex", flexDirection: "column", gap: 8 },
  listItem: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: "#1e293b", borderRadius: 10, border: "1px solid #334155" },
  empty: { padding: 40, textAlign: "center", color: "#64748b", fontSize: 14 },
  phone: { fontWeight: 600, fontSize: 15 },
  name: { color: "#94a3b8", fontSize: 14 },
  date: { fontSize: 12, color: "#64748b", marginTop: 4 },
  badge: { display: "inline-block", padding: "2px 8px", borderRadius: 4, background: "#164e63", color: "#22d3ee", fontSize: 11, fontWeight: 600, marginLeft: 8 },
  badgePajak: { display: "inline-block", padding: "2px 8px", borderRadius: 4, background: "#4a1d6e", color: "#c084fc", fontSize: 11, fontWeight: 600, marginLeft: 8 },

  // Groups
  groupConfig: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 },
  groupCard: { background: "#1e293b", borderRadius: 12, padding: 20, border: "1px solid #334155" },
  groupId: { fontSize: 12, color: "#64748b", wordBreak: "break-all", margin: "8px 0" },
  groupActions: { display: "flex", gap: 6 },
  helpText: { color: "#94a3b8", fontSize: 13, marginBottom: 16 },

  // Filters
  filterGroup: { display: "flex", gap: 4 },
  filterBtn: { padding: "6px 14px", borderRadius: 6, border: "1px solid #334155", background: "transparent", color: "#94a3b8", fontSize: 12, cursor: "pointer" },
  filterBtnActive: { background: "#334155", color: "#f1f5f9", borderColor: "#475569" },

  // Logs
  logList: { display: "flex", flexDirection: "column", gap: 12 },
  logItem: { background: "#1e293b", borderRadius: 10, padding: 16, border: "1px solid #334155" },
  logHeader: { display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" },
  logPhone: { fontWeight: 600, fontSize: 14 },
  logTime: { fontSize: 12, color: "#64748b", marginLeft: "auto" },
  logMsg: { fontSize: 13, color: "#e2e8f0", marginBottom: 6, padding: "8px 12px", background: "#0f172a", borderRadius: 8 },
  logReply: { fontSize: 13, color: "#94a3b8", padding: "8px 12px", background: "#0f172a", borderRadius: 8 },

  // Warning
  warningBox: { padding: "12px 16px", background: "#451a03", borderRadius: 8, color: "#fbbf24", fontSize: 13, marginBottom: 16, border: "1px solid #78350f" },
};

export default DevDashboard;
